import insane from 'insane'

/**
 * Configuration for sanitizing rich text content (notes, comments, etc.)
 * Allows safe HTML tags for rich text editing while preventing XSS attacks
 */
export const RICH_TEXT_SANITIZE_CONFIG = {
  allowedTags: [
    // Block elements
    'p', 'br', 'div',
    // Text formatting
    'strong', 'b', 'em', 'i', 'u', 's', 'strike',
    // Links
    'a',
    // Lists
    'ul', 'ol', 'li',
    // Blockquote
    'blockquote',
    // Code
    'code', 'pre',
    // Tables
    'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
    // h1-h6 are NOT allowed: notes don't render headings (TipTap StarterKit
    // has heading: false). Headings are converted to <strong> by
    // convertHeadingsToBold() before insane runs (insane.filter can't
    // rename tags — its allowedTags check runs before the filter callback).
  ],
  allowedAttributes: {
    a: ['href', 'target', 'rel', 'class'],
    code: ['class'],
    pre: ['class'],
    // Block elements may need class for styling
    p: ['class'],
    blockquote: ['class'],
    ul: ['class'],
    ol: ['class'],
    li: ['class'],
    // Table elements
    table: ['class'],
    th: ['class', 'colspan', 'rowspan'],
    td: ['class', 'colspan', 'rowspan'],
  },
  allowedClasses: {
    a: ['link'],
    code: ['language-*'],
    pre: ['language-*'],
  },
  // Filter function to validate URLs (prevent javascript: and data: URIs)
  filter: node => {
    // Check links for safe protocols
    if (node.tag === 'a' && node.attrs?.href) {
      const href = node.attrs.href.toLowerCase().trim()
      // Only allow http, https, mailto, and tel protocols
      const safeProtocols = ['http://', 'https://', 'mailto:', 'tel:', '//', '/']
      const isSafe = safeProtocols.some(protocol => href.startsWith(protocol))
        || (!href.includes(':') && !href.startsWith('//')) // Relative URLs without protocol
      if (!isSafe) {
        // Remove unsafe href
        delete node.attrs.href
      }
      // Add security attributes to external links
      if (node.attrs.href && (href.startsWith('http://') || href.startsWith('https://'))) {
        node.attrs.target = '_blank'
        node.attrs.rel = 'noopener noreferrer'
      }
    }
    return true
  },
}

/**
 * Convert any <h1>-<h6> tags in HTML to <strong>. Notes don't render
 * headings; the backend's transformTags catches future writes, and this
 * helper catches legacy stored content (e.g. contentPreview baked at write
 * time before the backend update) at render time.
 * @param {string} html - The HTML string
 * @returns {string} - HTML with heading tags rewritten to <strong>
 */
export function convertHeadingsToBold(html) {
  if (!html) return ''
  return html.replace(/<(\/?)[hH][1-6]\b[^>]*>/g, (_match, slash) => `<${slash}strong>`)
}

/**
 * Sanitize rich text HTML content to prevent XSS attacks
 * @param {string} html - The HTML string to sanitize
 * @returns {string} - Sanitized HTML string
 */
export function sanitizeRichText(html) {
  if (!html) return ''
  return insane(convertHeadingsToBold(html), RICH_TEXT_SANITIZE_CONFIG)
}

/**
 * Strip all HTML tags and return plain text
 * Useful for preview text, search indexing, etc.
 * @param {string} html - The HTML string to strip
 * @returns {string} - Plain text without HTML tags
 */
export function stripHtml(html) {
  if (!html) return ''
  // Use insane with no allowed tags to strip everything
  return insane(html, { allowedTags: [] })
}

/**
 * Extract plain text from HTML content
 * Uses DOMParser for proper text extraction (handles nested tags, entities, etc.)
 * Preferred over stripHtml for displaying readable text content
 * @param {string} html - The HTML string to extract text from
 * @returns {string} - Plain text content
 */
export function htmlToPlainText(html) {
  if (!html) return ''
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')
  return (doc.body.textContent || '').trim()
}

/**
 * Get plain text length from HTML content
 * Useful for character count validation
 * @param {string} html - The HTML string
 * @returns {number} - Character count of plain text content
 */
export function getPlainTextLength(html) {
  return stripHtml(html).length
}

export default {
  sanitizeRichText,
  convertHeadingsToBold,
  stripHtml,
  htmlToPlainText,
  getPlainTextLength,
  RICH_TEXT_SANITIZE_CONFIG,
}
